import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime, MonthNumbers } from 'luxon';
import { object, string } from 'yup';

import CTA from 'Src/components/buttons/CTA';
import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import { ToastAutoHideDuration } from 'Src/consts/duration';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { UATCustomerEnrollment } from 'Src/feature/loyalty/loyaltyService';
import { useLoyalty } from 'Src/feature/loyalty/useLoyalty';
import { useOnErrorShowToast } from 'Src/feature/toasts/useOnErrorShowToast';
import { useToast } from 'Src/feature/toasts/useToast';

import styles from './LoyaltyCustomerOnboardTool.module.scss';

const initialValues = {
  birthdayMonth: 1 as MonthNumbers,
  birthdayDay: 1,
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  password: undefined as string | undefined,
};

const monthDayOpts = (num: number, isMonths: boolean) => Array.from({ length: num }, (_, i) => ({
  value: i + 1,
  label: !isMonths ? `${i + 1}` : DateTime.fromObject({ month: i+1, day: 1, year: 2000 }).monthShort,
}));

const monthOpts = monthDayOpts(12, true);

type FormModel = typeof initialValues;

const { Form, Field } = getTypedFormComponents<FormModel>();
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const sectionId = 'LoyaltyCustomerOnboardTool';

const LoyaltyCustomerOnboardTool = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const handleEnrollError = useOnErrorShowToast({ title: t('devTool.loyaltyCustomerOnboard.errors.enroll') });
  const { mutate: { customers }, query: { program } } = useLoyalty;
  const enrollmentStatus = program.getEnrollmentStatus();
  const { mutate: enrollCustomer } = customers.devTool.enrollCustomer(handleEnrollError);
  const [dayOptions, setDayOptions] = useState(monthDayOpts(31, false)); // default to Jan

  console.table(dayOptions);

  const form = useForm<FormModel>({
    defaultValues: initialValues,
    schema: object().shape({
      firstName: string().label('First Name').required(),
      lastName: string().label('Last Name').required(),
      email: string().label('Email (username)').required(),
      phone: string().label('Phone Number').required(),
      password: string().label('Password').notRequired(),
    }),
  });

  const onSubmit = async () => {
    await form.action.submitIfValid(async ({ values }) => {
      const customerSignUp = {
        ...values,
        birthDay: values.birthdayDay,
        birthMonth: values.birthdayMonth,
        phoneNumber: `+1${values.phone.replace(/[^\d]/g, '')}`, //BDG: Loyalty system uses 'e164' validation on phone number (formatted like +1123456789): https://github.com/search?q=repo%3Anabancard%2Floyalty%20e164&type=code
      };
      enrollCustomer(customerSignUp as unknown as UATCustomerEnrollment, {
        onSuccess: () => {
          toast.make({
            id: `UAT.customer.signUp.${values.email}`,
            type: 'alert',
            autoHideDuration: ToastAutoHideDuration.Default,
            props: {
              title: 'Success!',
              message: `Customer ${values.email} is signed up for loyalty at this location.`,
              severity: 'success',
            },
          }).show();
          form.action.reset();
        },
      });
    });
  };

  if (enrollmentStatus.data && !enrollmentStatus.data.isEnrolled) {
    return <Copy variant="h3" color="error">This Location is not enrolled in a Loyalty Program.</Copy>;
  }

  return (
    <div>
      <Copy variant="h3" value="Onboard Loyalty Customer - UAT Dev Tool" />
      <br/>
      <Copy variant="caption" value="password only used in backend if customer is new" />
      <Form className={styles['form']} usedForm={form} fullWidth size="sm" omitChangeTracking>
        <Field type="email" name="email" label="Email (username)" />
        <Field type="password" name="password" label="Password" helperText="10 chars, 1 upper, 1 lower, 1 number, 1 special" />
        <Field type="text" name="firstName" label="First Name" />
        <Field type="text" name="lastName" label="Last Name" />
        <Field type="tel" name="phone" label="Phone" />
        <Flex className={styles['birthMonthDay']} horizontal>
          <Field className={styles['select']}
            type="select"
            name="birthdayMonth"
            label="Birth Month"
            options={monthOpts}
            onAfterChange={({ value }) => {
              //2024 to force leap year to get max days in Feb
              const daysInMonth = DateTime.fromObject({ month: value, day: 1, year: 2024 }).daysInMonth as number;
              const dayOpts = monthDayOpts(daysInMonth, false);
              if (!dayOpts.find((d) => d.value === form.action.getValues().birthdayDay)) {
              // then we flipped to a month where current day selection isn't valid
              // e.g. 10/31 -> change month to 02 (only 29 days)
                form.action.setValue('birthdayDay', 1);
              }
              setDayOptions(dayOpts);
            }} />
          <Copy variant="body" weight="bold" value="/" />
          <Field className={styles['select']} type="select" name="birthdayDay" label="Birth Day" options={dayOptions} />
        </Flex>
      </Form>
      <CTA color="primary" onClick={() => void onSubmit()} label="Enroll" />
    </div>
  );
};

export { LoyaltyCustomerOnboardTool };
